import { defineStore } from 'pinia'
import _ from 'lodash'

import { useCompaniesStore } from './companies'
import { useFiltersStore } from './filters'
import { useSegmentsStore } from './segments'
import { useInsightsRootStore } from './insights-root'
import { SfLog } from '@app/adapters/logger'
import api from '../api'
import serialize from '../utils/serialize'

interface Route {
  query: Record<string, string | undefined>
}

interface UrlParams {
  segment_id?: number | string | null
  partner_segment_id?: number | string | null
  filters?: string
  period_date?: string
  period_lock_type?: string
  period_selection?: string
  requestRef?: string
  ms_id?: string
  ms_name?: string
  debug?: string
  openFilterModal?: string
  [key: string]: unknown
}

export const useNavigationStore = defineStore('insights-navigation', () => {
  const segmentsStore = useSegmentsStore()

  function cancelCurrentSegmentlessRun(route: Route): void {
    const {
      segment_id: currentSegmentId,
      partner_segment_id: currentPartnerSegmentId,
    } = route.query
    const companiesStore = useCompaniesStore()
    if (
      !currentSegmentId &&
      !currentPartnerSegmentId &&
      companiesStore.pendingCompaniesRequest &&
      companiesStore.pendingCompaniesRequest.ref
    ) {
      api.cancelPendingRequest({
        pending_request_ref: companiesStore.pendingCompaniesRequest.ref,
      })
    }
  }

  function stateToUrlParams(route: Route): UrlParams {
    const params: UrlParams = _.pick(route.query, [
      'ms_id',
      'ms_name',
      'debug',
      'openFilterModal',
    ])

    if (segmentsStore.segment) {
      if (segmentsStore.segment.partner_segment_id) {
        params.partner_segment_id = segmentsStore.segment.partner_segment_id
      } else {
        params.segment_id = segmentsStore.segment.id
      }
    }

    if (!segmentsStore.segment || segmentsStore.isSegmentModified) {
      const filtersStore = useFiltersStore()
      const rootStore = useInsightsRootStore()
      params.filters = JSON.stringify(
        serialize.filterSettings(
          filtersStore.activeFilters,
          filtersStore.allFiltersByType
        )
      )
      if (rootStore.ledgerSelectDate) {
        params.period_date = rootStore.ledgerSelectDate
      }
      if (rootStore.ledgerSelectLockType) {
        params.period_lock_type = rootStore.ledgerSelectLockType
      }
      if (rootStore.periodSelection) {
        params.period_selection = rootStore.periodSelection
      }
      const companiesStore = useCompaniesStore()
      if (
        companiesStore.pendingCompaniesRequest &&
        filtersStore.getActiveFilters.length
      ) {
        params.requestRef = companiesStore.pendingCompaniesRequest.ref
      }
    }
    return params
  }

  function isCleanSegmentUrl(
    query: Record<string, string | undefined>,
    segmentId: number | null,
    partnerSegmentId: number | null | undefined
  ): boolean {
    const routeSegmentId = query.segment_id
      ? parseInt(query.segment_id, 10)
      : null

    const routePartnerSegmentId = query.partner_segment_id
      ? parseInt(query.partner_segment_id, 10)
      : null

    if (partnerSegmentId) {
      // Partner segments live under partner_segment_id alone.
      if (routePartnerSegmentId !== partnerSegmentId) return false
      if (routeSegmentId !== null) return false
    } else {
      if (routeSegmentId !== segmentId) return false
      if (routePartnerSegmentId !== null) return false
    }

    const allowed = new Set(['segment_id', 'partner_segment_id'])
    for (const [key, value] of Object.entries(query)) {
      if (value === null || value === undefined || value === '') continue
      if (!allowed.has(key)) return false
    }

    return true
  }

  function storeDataInUrl(route: Route): { query: UrlParams } | undefined {
    try {
      if (segmentsStore.segment && !segmentsStore.isSegmentModified) {
        const segmentId = segmentsStore.segment.id
        const partnerSegmentId = segmentsStore.segment.partner_segment_id
        // On a clean segment the URL should be just {segment_id} for firm
        // segments or {partner_segment_id} for partner segments. Skip the
        // router push only when the current route already matches that shape
        // exactly — i.e. the right id key is set AND no stale query params
        // (filters, period_*, requestRef, etc.) remain. Otherwise we must
        // push so those extras get cleared (e.g. after the last filter is
        // removed).
        if (isCleanSegmentUrl(route.query, segmentId, partnerSegmentId)) {
          return undefined
        }

        return {
          query: partnerSegmentId
            ? { partner_segment_id: partnerSegmentId }
            : { segment_id: segmentId },
        }
      } else {
        const params = stateToUrlParams(route)
        return { query: params }
      }
    } catch (e) {
      SfLog.warn('Failed to store data in URL', {
        extra: { error: e },
      })
    }
  }

  function loadStateFromUrlQueryParams(
    params: Record<string, string | undefined>
  ): void {
    if (params.segment_id || params.partner_segment_id) {
      const partnerSegmentId = params.partner_segment_id
        ? parseInt(params.partner_segment_id, 10)
        : undefined

      const segmentId = params.segment_id
        ? parseInt(params.segment_id, 10)
        : (partnerSegmentId as number)

      void segmentsStore.loadSegment({
        id: segmentId,
        partner_segment_id: partnerSegmentId,
      })
    }

    const rootStore = useInsightsRootStore()
    if (params.period_date) {
      rootStore.setLedgerSelectDate(params.period_date)
    }
    if (params.period_lock_type) {
      rootStore.setLedgerSelectLockType(params.period_lock_type)
    }
    if (params.period_selection) {
      rootStore.setPeriodSelection(params.period_selection)
    }

    if (params.filters) {
      try {
        const filtersStore = useFiltersStore()
        const filters = JSON.parse(params.filters) as Array<
          Record<string, unknown>
        >
        filters.forEach((filter) => {
          const filterType = filtersStore.allFilters.find(
            (typeConfig) => typeConfig.type === filter.type
          )
          // A URL can outlive the filter it names: bookmarks and shared links keep working long
          // after a filter type is removed. Restoring one the frontend no longer knows adds a
          // column with no title and no working edit modal, and the backend rejects the type
          // outright, so the whole query fails.
          if (!filterType) {
            SfLog.warn('Ignoring unknown filter type from URL', {
              extra: { filterType: filter.type },
            })
            return
          }
          const deserialize = filterType.deserializeSettings as
            | ((params: unknown) => Record<string, unknown>)
            | undefined
          filtersStore.addOrUpdateFilter({
            uuid: filter.uuid as string | undefined,
            filterType: filter.type as string,
            settings: deserialize
              ? deserialize(filter.params)
              : (filter.settings as Record<string, unknown>),
            columnName: filter.column_name as string | undefined,
          })
        })
      } catch (e) {
        SfLog.warn(
          'Failed to parse filters from URL — continuing with empty filters',
          {
            extra: { filters: params.filters, error: e },
          }
        )
      }
    }

    if (params.openFilterModal) {
      const filtersStore = useFiltersStore()
      const filter = filtersStore.getActiveFilters.find(
        (f) => f.uuid === params.openFilterModal
      )

      if (filter) {
        const fullFilter = {
          ...filter,
          ...filtersStore.allFiltersByType[filter.type],
        }
        filtersStore.editFilter(fullFilter)
      }
    }
  }

  function handleFilterOrPeriodChange(
    route: Route
  ): { query: UrlParams } | undefined {
    try {
      void useCompaniesStore().fetchFilteredCompanies({
        existingRequestRef: useInsightsRootStore().existingRequestRef,
      })
    } catch (e) {
      SfLog.warn(
        'Error requesting Insights companies on filter/period change',
        {
          extra: {
            firmId: window.Silverfin.currentFirmId,
            error: e,
          },
        }
      )
    }

    useInsightsRootStore().setExistingRequestRef(null)

    return storeDataInUrl(route)
  }

  return {
    cancelCurrentSegmentlessRun,
    loadStateFromUrlQueryParams,
    handleFilterOrPeriodChange,
    storeDataInUrl,
    stateToUrlParams,
  }
})
